import React, { useState, useEffect } from 'react'

import { localizePath } from '../../utils/helpers/localization/localize'
import Markdown from '../parts/markdown'
import LocaleSwitch from '../elements/locale-switch'
import SocialLinks from '../elements/social-links'
import MainNav from '../elements/nav/nav-main'
import NavUtils from '../elements/nav/nav-utils'
import NavUtilsNew from '../elements/nav/nav-utils-new'
import Image from '../parts/image'
import Link from '../parts/link'
import Button from '../parts/button'

const Header = ({ pageContext, data, theme, children, scrollLock, contextualNav: ContextualNav }) => {
  if (!data) {
    return ''
  }

  const isBrowser = typeof window !== 'undefined'
  const [scrolled, setScrolled] = useState(false)
  const themeModifier = data.themeModifier ? data.themeModifier : 'none'
  const socialLinks = data?.socialData
  const mainNav = data?.mainNavData
  const ctaLinks = data?.ctaData
  const brand = data?.brandData
  const strings = data?.stringsData

  // Stick main nav to top on scroll
  const handleScroll = () => {
    const offset = isBrowser ? window.scrollY : 0
    if (offset >= 250) {
      setScrolled(true)
    } else {
      setScrolled(false)
    }
  }
  useEffect(() => {
    if (isBrowser) {
      window.addEventListener('scroll', handleScroll)
    }
  })

  return (
    <header
      className={`header-main sticky top-0 z-god transition-all pointer-events-none 
        ${theme && theme.MainNav !== null && theme.MainNav.mobileNav !== null
          ? (theme.MainNav.mobileNav === 'light'
            ? 'header-light'
            : (theme.MainNav.mobileNav === 'hamburger'
              ? 'header-hamburger'
              : (theme.MainNav.mobileNav === 'scrollable'
                ? 'header-scrollable'
                : 'header-default'))
          )
          : 'header-default'}
    `}>
      {!theme?.NavUtils?.disabled && (
        <NavUtilsNew
          locale={pageContext.locale}
          project={pageContext.dataLayer?.game ? pageContext.dataLayer?.game : pageContext.customDataLayer?.game}
          className={`relative flex flex-row items-center justify-end px-5 lg:px-8 py-2 lg:py-4 pointer-events-auto h-16 lg:h-[55px] box-border ${theme && theme.NavUtils && theme.NavUtils.bg ? `bg-${theme.NavUtils.bg}` : 'bg-black'}`}
        />
      )}
      <div
        className={`nav flex justify-between px-5 lg:pl-16 lg:pr-0 h-[45px] lg:h-32 relative pointer-events-auto
          ${scrolled
            ? 'scrolled'
            : 'initial'
          }
          ${theme && theme.MainNav && theme.MainNav !== null && (`
            ${theme.MainNav.bg
              ? `bg-${theme.MainNav.bg}`
              : 'bg-secondary'
            } 
          `)}
          ${themeModifier.hideMainNav && (
            'nav-disabled'
          )}
        `}
      >
        <div className="flex w-auto items-center relative z-10">
          <Link
            to={localizePath({ ...pageContext, slug: '' })}
            className="inline-flex drop-shadow-none hover:drop-shadow-2xl transition-all duration-300"
          >
            <Image
              media={brand.fullLogo}
              className={`nav-logo ${scrolled ? 'scrolled' : 'initial'} ${brand.logoMobile && brand.logoMobile !== null && 'gatsby-hidden-mobile'}`}
            />
            {brand.logoMobile && brand.logoMobile !== null && (
              <Image
                media={brand.logoMobile}
                className={`nav-logo ${scrolled ? 'scrolled' : 'initial'} gatsby-hidden-desktop`}
              />
            )}
          </Link>
        </div>
        {!themeModifier.hideMainNav && (
          <MainNav
            links={mainNav}
            brand={brand}
            theme={theme?.MainNav ? theme.MainNav : null}
            scrollLock={scrollLock}
            pageContext={pageContext}
            className={theme?.MainNav?.mobileNav ? `nav-${theme.MainNav.mobileNav} ${theme.MainNav?.nav ? theme.MainNav?.nav : ''}` : 'nav-default w-auto h-full list-none justify-between text-center transition-all lg:flex lg:flex-row lg:items-center font-sans border-0 absolute lg:relative left-0 tracking-wider lg:px-12'}
            eventPage={data?.dataLayer?.trackingPageName}
          >
            <li
              className="nav-item nav-item-locales flex w-full cursor-default flex-col items-center font-bold lg:w-auto shrink-0 lg:hidden"
              key={`mainnav-link-locales`}
            >
              {pageContext.localizations && (
                <LocaleSwitch
                  className="font-display text-md tracking-wider text-white py-8 pl-20 pr-12"
                  pageContext={pageContext}
                  selectorIcon={theme?.LocaleSwitch?.selectorIcon}
                  languageIcon={theme?.LocaleSwitch?.languageIcon}
                  layout={theme?.LocaleSwitch?.layout}
                  title={strings?.labels?.language}
                />
              )}
            </li>
            <li
              className="nav-item nav-item-cta sticky-top-mobile sticky flex flex-col items-center space-y-8 overflow-hidden p-10 pt-20 lg:hidden"
              key={`mainnav-link-ctas`}
            >
              {ctaLinks !== null && ctaLinks.map((ctaLink) => (
                <Button
                  id={`${ctaLink.buttonId}Mobile`}
                  link={ctaLink.link}
                  trigger={ctaLink.trigger}
                  size="lg"
                  style={ctaLink.style}
                  icon={ctaLink.icon}
                  pageContext={pageContext}
                  gsEvent={ctaLink?.gsEvent}
                  eventCategory={ctaLink.eventCategory ? ctaLink.eventCategory : 'buy'}
                  eventAction={ctaLink.eventAction ? ctaLink.eventAction : ''}
                  eventLabel={ctaLink.eventLabel}
                  eventElement={ctaLink.eventElement}
                  eventPage={data?.dataLayer?.trackingPageName}
                  eventSection={ctaLink.eventSection}
                  eventType={ctaLink.eventCategory === 'buy' ? (ctaLink.trigger === 'internalLink' ? 'internalLink' : 'externalLink') : 'form'}
                >
                  {ctaLink.buttonId && (
                    <span className="hidden">{ctaLink.buttonId}</span>
                  )}
                  <Markdown children={ctaLink.title} />
                </Button>
              ))}
            </li>
          </MainNav>
        )}
        <div className="nav-ctas flex h-full w-auto flex-row items-center justify-end z-10 relative">
          {!themeModifier.hideMainNav && theme?.MainNav?.social && (
            <>
              {socialLinks && socialLinks !== null && (
                <SocialLinks
                  pageContext={pageContext}
                  social={socialLinks}
                  className="flex gap-x-8"
                  iconClassName="social-links-item-round"
                  display={theme?.MainNav?.socialDisplay || 'X'}
                  eventSection="nav"
                />
              )}
            </>
          )}
          {pageContext.localizations && pageContext.localizations.data.length > 0 && (
            <div
              key="mainnav-language"
              className="nav-item-locales relative hidden h-full flex-col items-center justify-between px-8 text-md text-white lg:flex"
            >
              <LocaleSwitch
                className="text-white"
                pageContext={pageContext}
                selectorIcon={theme?.LocaleSwitch?.selectorIcon}
                languageIcon={theme?.LocaleSwitch?.languageIcon}
                layout={theme?.LocaleSwitch?.layout}
                title={strings?.labels?.language}
              />
            </div>
          )}
          {ctaLinks !== null && ctaLinks.map((ctaLink) => (
            !ctaLink.mobileOnly && (
              <Button
                id={ctaLink.buttonId}
                link={ctaLink.link}
                trigger={ctaLink.trigger}
                size="lg"
                icon={ctaLink.icon}
                pageContext={pageContext}
                gsEvent={ctaLink?.gsEvent}
                eventCategory={
                  ctaLink.eventCategory ? ctaLink.eventCategory : ''
                }
                eventAction={ctaLink.eventAction ? ctaLink.eventAction : ''}
                eventLabel={ctaLink.eventLabel}
                eventElement={ctaLink.eventElement}
                eventPage={data?.dataLayer?.trackingPageName}
                eventSection={ctaLink.eventSection}
                eventType={ctaLink.eventCategory === 'buy' ? (ctaLink.trigger === 'internalLink' ? 'internalLink' : 'externalLink') : 'form'}
              >
                {ctaLink.buttonId && (
                  <span className="hidden">{ctaLink.buttonId}</span>
                )}
                <Markdown children={ctaLink.title} />
              </Button>
            )
          ))}
        </div>
        {/* DYNAMIC ZONE - For displaying custom content in header */}
        {children && children !== null && (
          children
        )}
      </div>
      {ContextualNav && (
        <ContextualNav />
      )}
    </header>
  )
}

export default Header